var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { MULTIPLAYER_OTEL_DEFAULT_TRACES_EXPORTER_HTTP_URL, MULTIPLAYER_TRACE_DEBUG_PREFIX, MULTIPLAYER_TRACE_CONTINUOUS_DEBUG_PREFIX, MULTIPLAYER_TRACE_SESSION_CACHE_PREFIX, } from '../constants/constants.base';
/**
 * Browser-specific trace exporter for Session Recorder
 * Exports traces via HTTP to Multiplayer's OTLP endpoint with browser-specific optimizations
 * Only exports spans with trace IDs starting with Multiplayer prefixes
 */
var SessionRecorderBrowserTraceExporter = /** @class */ (function () {
    function SessionRecorderBrowserTraceExporter(config) {
        if (config === void 0) { config = {}; }
        this.usePostMessage = false;
        var _a = config.url, url = _a === void 0 ? MULTIPLAYER_OTEL_DEFAULT_TRACES_EXPORTER_HTTP_URL : _a, apiKey = config.apiKey, _b = config.headers, headers = _b === void 0 ? {} : _b, _c = config.timeoutMillis, timeoutMillis = _c === void 0 ? 30000 : _c, _d = config.keepAlive, keepAlive = _d === void 0 ? true : _d, _e = config.concurrencyLimit, concurrencyLimit = _e === void 0 ? 20 : _e, _f = config.postMessageType, postMessageType = _f === void 0 ? 'MULTIPLAYER_SESSION_DEBUGGER_LIB' : _f, _g = config.postMessageTargetOrigin, postMessageTargetOrigin = _g === void 0 ? '*' : _g;
        this.config = __assign(__assign({}, config), { url: url, apiKey: apiKey, headers: headers, keepAlive: keepAlive, timeoutMillis: timeoutMillis, concurrencyLimit: concurrencyLimit });
        this.postMessageType = postMessageType;
        this.postMessageTargetOrigin = postMessageTargetOrigin;
        this.exporter = this._createExporter();
    }
    SessionRecorderBrowserTraceExporter.prototype._export = function (spans, resultCallback) {
        var _this = this;
        // Only proceed if there are filtered spans
        if (spans.length === 0) {
            resultCallback({ code: 0 });
            return;
        }
        if (this.usePostMessage) {
            this.exportViaPostMessage(spans, resultCallback);
            return;
        }
        this.exporter.export(spans, function (result) {
            if (result.code === 0) {
                resultCallback(result);
            }
            else if (_this.config.usePostMessageFallback) {
                _this.usePostMessage = true;
                _this.exportViaPostMessage(spans, resultCallback);
            }
            else {
                resultCallback(result);
            }
        });
    };
    SessionRecorderBrowserTraceExporter.prototype.export = function (spans, resultCallback) {
        // Filter spans to only include those with Multiplayer trace prefixes
        var filteredSpans = spans.filter(function (span) {
            var traceId = span.spanContext().traceId;
            return (traceId.startsWith(MULTIPLAYER_TRACE_DEBUG_PREFIX) ||
                traceId.startsWith(MULTIPLAYER_TRACE_CONTINUOUS_DEBUG_PREFIX));
        });
        this._export(filteredSpans, resultCallback);
    };
    SessionRecorderBrowserTraceExporter.prototype.exportBuffer = function (spans, resultCallback) {
        var filteredSpans = spans.filter(function (span) {
            var traceId = span.spanContext().traceId;
            return traceId.startsWith(MULTIPLAYER_TRACE_SESSION_CACHE_PREFIX);
        });
        this._export(filteredSpans, resultCallback);
    };
    SessionRecorderBrowserTraceExporter.prototype.shutdown = function () {
        return this.exporter.shutdown();
    };
    SessionRecorderBrowserTraceExporter.prototype.exportViaPostMessage = function (spans, resultCallback) {
        var _this = this;
        if (typeof window === 'undefined') {
            resultCallback({ code: 1 });
            return;
        }
        try {
            window.postMessage({
                action: 'traces',
                type: this.postMessageType,
                payload: spans.map(function (span) { return _this.serializeSpan(span); }),
            }, this.postMessageTargetOrigin);
            resultCallback({ code: 0 });
        }
        catch (e) {
            resultCallback({ code: 1 });
        }
    };
    SessionRecorderBrowserTraceExporter.prototype.serializeSpan = function (span) {
        var _a;
        var spanContext = span.spanContext();
        var instrumentationScope = 
        // OTel SDK (modern)
        span.instrumentationScope ||
            // Older SDKs
            span.instrumentationLibrary || { name: 'unknown', version: undefined, schemaUrl: undefined };
        var normalizedScope = {
            name: (instrumentationScope === null || instrumentationScope === void 0 ? void 0 : instrumentationScope.name) || 'unknown',
            version: instrumentationScope === null || instrumentationScope === void 0 ? void 0 : instrumentationScope.version,
            schemaUrl: instrumentationScope === null || instrumentationScope === void 0 ? void 0 : instrumentationScope.schemaUrl,
        };
        return {
            _spanContext: spanContext,
            traceId: spanContext.traceId,
            spanId: spanContext.spanId,
            name: span.name,
            kind: span.kind,
            links: span.links,
            ended: span.ended,
            events: span.events,
            status: span.status,
            endTime: span.endTime,
            startTime: span.startTime,
            duration: span.duration,
            attributes: span.attributes,
            parentSpanId: (_a = span.parentSpanContext) === null || _a === void 0 ? void 0 : _a.spanId,
            droppedAttributesCount: span.droppedAttributesCount,
            droppedEventsCount: span.droppedEventsCount,
            droppedLinksCount: span.droppedLinksCount,
            instrumentationScope: normalizedScope,
            resource: {
                attributes: span.resource.attributes,
                asyncAttributesPending: span.resource.asyncAttributesPending,
            },
        };
    };
    SessionRecorderBrowserTraceExporter.prototype._createExporter = function () {
        return new OTLPTraceExporter({
            url: this.config.url,
            headers: __assign(__assign({ 'Content-Type': 'application/json' }, (this.config.apiKey ? { Authorization: this.config.apiKey } : {})), (this.config.headers || {})),
            timeoutMillis: this.config.timeoutMillis,
            keepAlive: this.config.keepAlive,
            concurrencyLimit: this.config.concurrencyLimit,
        });
    };
    SessionRecorderBrowserTraceExporter.prototype.setApiKey = function (apiKey) {
        this.config.apiKey = apiKey;
        this.exporter = this._createExporter();
    };
    return SessionRecorderBrowserTraceExporter;
}());
export { SessionRecorderBrowserTraceExporter };
//# sourceMappingURL=SessionRecorderBrowserTraceExporter.js.map